import { useState } from 'react';
import { UserPlus, Users } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useAppSelector } from '@/app/hooks';
import { useAccess, useFormat } from '@/hooks/useAccess';
import {
  useCreateMemberMutation, useMembersQuery, useRemoveMemberMutation, useRolesQuery,
  useSetMemberPasswordMutation, useUpdateMemberMutation,
  type Member,
} from '@/api/endpoints';
import { apiErrorMessage, apiFieldErrors } from '@/api/baseApi';
import { formatDateTime, initials } from '@/lib/format';
import {
  Alert, Badge, Button, Card, Checkbox, ConfirmDialog, EmptyState, ErrorState, Input,
  LoadingState, Modal, PageHeader, Pagination, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { SummaryCards } from '@/components/SummaryCards';

const STATUS_TONE = {
  ACTIVE: 'success',
  INVITED: 'info',
  SUSPENDED: 'warning',
  REMOVED: 'neutral',
} as const;

export function TeamPage() {
  const format = useFormat();
  const { isOwner } = useAccess();
  // An owner's account is theirs alone; nobody else edits, deactivates,
  // removes or re-passwords it.
  const currentUserId = useAppSelector((state) => state.auth.user?.id);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const { data, isLoading, isError, error, refetch } = useMembersQuery({
    page,
    limit: 25,
    search: search || undefined,
  });
  const roles = useRolesQuery({ limit: 100 });
  const [createMember, createState] = useCreateMemberMutation();
  const [updateMember, updateState] = useUpdateMemberMutation();
  const [setMemberPassword, passwordState] = useSetMemberPasswordMutation();
  const [removeMember, removeState] = useRemoveMemberMutation();

  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);
  const [resetting, setResetting] = useState<Member | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [passwordNotice, setPasswordNotice] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<Member | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [form, setForm] = useState({
    email: '', firstName: '', lastName: '', phone: '', password: '', jobTitle: '',
    roleIds: [] as string[],
  });

  const fieldErrors = apiFieldErrors(editing ? updateState.error : createState.error);
  const saving = createState.isLoading || updateState.isLoading;
  const saveError = editing ? updateState.error : createState.error;

  const toggleRole = (roleId: string): void =>
    setForm((current) => ({
      ...current,
      roleIds: current.roleIds.includes(roleId)
        ? current.roleIds.filter((id) => id !== roleId)
        : [...current.roleIds, roleId],
    }));

  const blankForm = {
    email: '', firstName: '', lastName: '', phone: '', password: '', jobTitle: '',
    roleIds: [] as string[],
  };

  const startEdit = (member: Member): void => {
    setEditing(member);
    setForm({
      email: member.user.email,
      firstName: member.user.firstName,
      lastName: member.user.lastName,
      phone: member.user.phone ?? '',
      // An administrator changes what an account may do, not its password.
      password: '',
      jobTitle: member.jobTitle ?? '',
      roleIds: member.roles.map((role) => role.id),
    });
  };

  const submit = async (): Promise<void> => {
    const saved = editing
      ? await updateMember({
          id: editing.id,
          body: {
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            phone: form.phone,
            jobTitle: form.jobTitle,
            roleIds: form.roleIds,
          },
        })
          .unwrap()
          .catch(() => null)
      : await createMember(form).unwrap().catch(() => null);

    if (saved) {
      setInviting(false);
      setEditing(null);
      setForm(blankForm);
    }
  };

  /**
   * Turning an account off leaves everything it did in place — it simply stops
   * being able to sign in. Removing a member is the other action, and it is
   * still there beside this one.
   */
  const setActive = async (member: Member, active: boolean): Promise<void> => {
    setPasswordError(null);
    try {
      await updateMember({
        id: member.id,
        body: { status: active ? 'ACTIVE' : 'SUSPENDED' },
      }).unwrap();
      setPasswordNotice(
        active
          ? `${member.user.firstName} can sign in again.`
          : `${member.user.firstName} has been deactivated and cannot sign in.`,
      );
    } catch (error) {
      setPasswordError(apiErrorMessage(error, 'That account could not be changed'));
    }
  };

  const submitPassword = async (): Promise<void> => {
    if (!resetting) return;
    setPasswordError(null);
    try {
      const result = await setMemberPassword({ id: resetting.id, password: newPassword }).unwrap();
      setPasswordNotice(
        `Password set for ${resetting.user.firstName}. `
        + `${result.sessionsRevoked} session${result.sessionsRevoked === 1 ? '' : 's'} signed out.`,
      );
      setResetting(null);
      setNewPassword('');
    } catch (error) {
      setPasswordError(apiErrorMessage(error, 'That password could not be set'));
    }
  };

  const confirmRemove = async (): Promise<void> => {
    if (!pendingRemove) return;
    setRemoveError(null);
    try {
      await removeMember(pendingRemove.id).unwrap();
      setPendingRemove(null);
    } catch (removalError) {
      setRemoveError(apiErrorMessage(removalError, 'This member could not be removed'));
    }
  };

  return (
    <>
      {passwordNotice && <div className="mb-4"><Alert tone="success">{passwordNotice}</Alert></div>}

      <PageHeader
        title="Team"
        description="Who has access to this business, and what they are allowed to do."
        action={
          <Can permission={P.USER_INVITE}>
            <Button icon={UserPlus} onClick={() => { setForm(blankForm); setInviting(true); }}>
              New team member
            </Button>
          </Can>
        }
      />

      {data?.summary && (
        <SummaryCards
          stats={[
            { label: 'Team', value: String(data.summary.count) },
            { label: 'Active', value: String(data.summary.active), hint: 'can sign in now' },
            data.summary.invited
              ? {
                  label: 'Invited',
                  value: String(data.summary.invited),
                  hint: 'never signed in yet',
                }
              : null,
            data.summary.suspended
              ? {
                  label: 'Suspended',
                  value: String(data.summary.suspended),
                  hint: 'access withdrawn',
                }
              : null,
          ]}
        />
      )}

      <Card>
        <div className="border-b border-subtle p-4">
          <div className="max-w-sm">
            <Input
              label="Search"
              placeholder="Name or email"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={Users}
            title={search ? 'Nobody matches that search' : 'No team members yet'}
            description={search ? undefined : 'Create an account for your first colleague.'}
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Member</Th>
                  <Th>Roles</Th>
                  <Th>Access</Th>
                  <Th>Status</Th>
                  <Th>Last seen</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {data?.items.map((member) => (
                  <tr key={member.id} className="hover:bg-sunken">
                    <Td>
                      <div className="flex items-center gap-3">
                        <span className="grid size-9 shrink-0 place-items-center rounded-full bg-brand-100 text-xs font-semibold text-brand-800 dark:bg-brand-900 dark:text-brand-100">
                          {initials(member.user.firstName, member.user.lastName)}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-medium">
                            {member.user.firstName} {member.user.lastName}
                          </p>
                          <p className="truncate text-xs text-muted">{member.user.email}</p>
                        </div>
                      </div>
                    </Td>
                    <Td>
                      <div className="flex flex-wrap gap-1">
                        {member.isOwner && <Badge tone="brand">Owner</Badge>}
                        {member.roles
                          .filter((role) => !role.isOwnerRole)
                          .map((role) => (
                            <Badge key={role.id}>{role.name}</Badge>
                          ))}
                      </div>
                    </Td>
                    <Td className="text-sm text-secondary">
                      {member.allBranches ? 'All branches' : `${member.branchIds.length} branch(es)`}
                    </Td>
                    <Td>
                      <Badge tone={STATUS_TONE[member.status]}>{member.status.toLowerCase()}</Badge>
                    </Td>
                    <Td className="text-xs text-muted">
                      {member.user.lastLoginAt ? formatDateTime(member.user.lastLoginAt, format) : 'Never'}
                    </Td>
                    <Td className="text-right">
                      <Can permission={P.USER_MANAGE}>
                        {(!member.isOwner || member.user.id === currentUserId) && (
                          <Button size="sm" variant="ghost" onClick={() => startEdit(member)}>
                            Edit
                          </Button>
                        )}
                        {!member.isOwner && (
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={updateState.isLoading}
                            onClick={() => void setActive(member, member.status !== 'ACTIVE')}
                          >
                            {member.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                          </Button>
                        )}
                        {isOwner && !member.isOwner && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setPasswordError(null);
                              setNewPassword('');
                              setResetting(member);
                            }}
                          >
                            Set password
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={member.isOwner}
                          title={member.isOwner ? 'Owners cannot be removed here' : undefined}
                          onClick={() => {
                            setRemoveError(null);
                            setPendingRemove(member);
                          }}
                        >
                          Remove
                        </Button>
                      </Can>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {data && (
              <Pagination
                page={data.meta.page}
                totalPages={data.meta.totalPages}
                total={data.meta.total}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <Modal
        open={resetting !== null}
        onClose={() => setResetting(null)}
        size="sm"
        title={resetting ? `Set a password for ${resetting.user.firstName}` : 'Set a password'}
        description="They can sign in with it straight away."
        footer={
          <>
            <Button variant="outline" onClick={() => setResetting(null)}>Cancel</Button>
            <Button
              loading={passwordState.isLoading}
              disabled={newPassword.length < 8}
              onClick={() => void submitPassword()}
            >
              Set it
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {passwordError && <Alert tone="danger">{passwordError}</Alert>}
          <Alert tone="warning">
            Every session this account has open is signed out, so the old password stops working
            on a till that is still logged in.
          </Alert>
          <Input
            label="New password"
            type="text"
            required
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            hint="At least 8 characters. Shown in plain text because you are handing it over."
          />
        </div>
      </Modal>

      <Modal
        open={inviting || editing !== null}
        onClose={() => {
          setInviting(false);
          setEditing(null);
        }}
        title={editing ? `Edit ${editing.user.firstName} ${editing.user.lastName}` : 'New team member'}
        description={
          editing
            ? 'Their details and what the account may do.'
            : 'The account works immediately — hand the password over. What it may do is decided by the roles.'
        }
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setInviting(false);
                setEditing(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void submit()}
              loading={saving}
              disabled={
                !form.email
                || !form.firstName
                || !form.lastName
                || form.roleIds.length === 0
                || (!editing && form.password.length < 8)
              }
            >
              {editing ? 'Save changes' : 'Create the account'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {saveError && <Alert tone="danger">{apiErrorMessage(saveError)}</Alert>}

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="First name"
              required
              value={form.firstName}
              onChange={(event) => setForm({ ...form, firstName: event.target.value })}
              error={fieldErrors.firstName}
            />
            <Input
              label="Last name"
              required
              value={form.lastName}
              onChange={(event) => setForm({ ...form, lastName: event.target.value })}
              error={fieldErrors.lastName}
            />
          </div>

          <Input
            label="Email"
            type="email"
            required
            value={form.email}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
            error={fieldErrors.email}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Phone"
              value={form.phone}
              onChange={(event) => setForm({ ...form, phone: event.target.value })}
              error={fieldErrors.phone}
            />
            <Input
              label="Job title"
              value={form.jobTitle}
              onChange={(event) => setForm({ ...form, jobTitle: event.target.value })}
            />
          </div>

          {!editing && (
            <Input
              label="Password"
              type="text"
              required
              value={form.password}
              onChange={(event) => setForm({ ...form, password: event.target.value })}
              error={fieldErrors.password}
              hint="At least 8 characters. Shown in plain text because you are handing it over."
            />
          )}

          <fieldset>
            <legend className="mb-2 text-sm font-medium">Roles</legend>
            {roles.isLoading ? (
              <LoadingState label="Loading roles…" />
            ) : (
              <div className="space-y-2 rounded-lg border border-subtle p-3">
                {roles.data?.items
                  .filter((role) => !role.isOwnerRole)
                  .map((role) => (
                    <Checkbox
                      key={role.id}
                      label={role.name}
                      description={role.description ?? undefined}
                      checked={form.roleIds.includes(role.id)}
                      onChange={() => toggleRole(role.id)}
                    />
                  ))}
              </div>
            )}
            {fieldErrors.roleIds && <p className="mt-2 text-sm text-red-600">{fieldErrors.roleIds}</p>}
          </fieldset>
        </div>
      </Modal>

      <ConfirmDialog
        open={pendingRemove !== null}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => void confirmRemove()}
        title="Remove this member?"
        message={
          removeError ??
          `${pendingRemove?.user.firstName} ${pendingRemove?.user.lastName} will lose access immediately. Everything they recorded stays in the audit trail.`
        }
        confirmLabel="Remove"
        loading={removeState.isLoading}
      />
    </>
  );
}
